import { z } from "zod"

import {
  apiKeyScopes,
  organizationCollectsFromValues,
  organizationRoles,
} from "../../primitives.js"
import { ClientBrandFieldsSchema } from "./client-brand.js"
import { ApiSlugSchema } from "./common.js"
import { ApiUserSummarySchema } from "./users.js"

export const ApiOrganizationSchema = z.object({
  organizationId: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  billingEmail: z.string().nullable(),
  role: z.enum(organizationRoles).nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Optional so older API deployments still parse; set once orgs can archive.
  archivedAt: z.string().datetime().nullable().optional(),
  // Admin-chosen workspace fresh sessions land in; null falls back to the
  // first active workspace. Optional so older API deployments still parse.
  defaultWorkspaceId: z.string().uuid().nullable().optional(),
  // What the org collects documents for; null until onboarding asks. Optional
  // so older API deployments still parse.
  collectsFrom: z.enum(organizationCollectsFromValues).nullable().optional(),
  // Stamped once when onboarding finishes; null sends an admin to /welcome.
  // Optional so older API deployments still parse.
  onboardingCompletedAt: z.string().datetime().nullable().optional(),
})

export type ApiOrganization = z.infer<typeof ApiOrganizationSchema>

/** The portal contact reminders address by name. */
export const ApiWorkspacePrimaryContactSchema = z.object({
  contactId: z.string().uuid(),
  name: z.string().nullable(),
  email: z.string().email(),
})

export type ApiWorkspacePrimaryContact = z.infer<
  typeof ApiWorkspacePrimaryContactSchema
>

/** Portal state for one client. Returned only with includeClientDetails=true. */
export const ApiWorkspaceClientDetailSchema = z.object({
  /** True when the client has a portal link. */
  portalLinked: z.boolean(),
  /** Contacts that can still sign in. */
  contactCount: z.number().int().nonnegative(),
  /** Null when no contact is chosen, which the client settings form asks for. */
  primaryContact: ApiWorkspacePrimaryContactSchema.nullable(),
})

export type ApiWorkspaceClientDetail = z.infer<
  typeof ApiWorkspaceClientDetailSchema
>

export const ApiWorkspaceSchema = z.object({
  workspaceId: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  clientCompanyName: z.string().nullable(),
  externalReference: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  archivedAt: z.string().datetime().nullable(),
  // Client fields. Optional so older API deployments still parse.
  // clientSince is null for a workspace that holds the firm's own books.
  clientSince: z.string().datetime().nullable().optional(),
  clientNotes: z.string().nullable().optional(),
  primaryContactId: z.string().uuid().nullable().optional(),
  // What the firm keeps about how the client looks. Optional so older API
  // deployments still parse.
  clientWebsite: z.string().nullable().optional(),
  clientLegalName: z.string().nullable().optional(),
  clientLogoDataUrl: z.string().nullable().optional(),
  clientBrandColor: z.string().nullable().optional(),
  // Present only when the list was asked for client details.
  client: ApiWorkspaceClientDetailSchema.nullable().optional(),
})

export type ApiWorkspace = z.infer<typeof ApiWorkspaceSchema>

// TODO(LED-46-placeholder): Documents v2 design uses Owner / Admin / Bookkeeper /
// Reviewer / Read-only. Backend ships only `admin | member` today. UI renders
// the design's 5-role descriptions as static copy and binds visibility to the
// existing two-role enum. Revisit when a real RBAC overhaul is scheduled.
export const ApiOrganizationMemberSchema = z.object({
  memberId: z.string().uuid(),
  organizationId: z.string().uuid(),
  userId: z.string(),
  role: z.enum(organizationRoles),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  deactivatedAt: z.string().datetime().nullable(),
  // Mirrored Clerk profile; null when the user has no local row yet and the
  // backfill fetch failed. Optional so older API deployments still parse.
  user: ApiUserSummarySchema.nullable().optional(),
})

export type ApiOrganizationMember = z.infer<typeof ApiOrganizationMemberSchema>

export const ApiKeyMetadataSchema = z.object({
  apiKeyId: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  keyPrefix: z.string(),
  scopes: z.array(z.enum(apiKeyScopes)),
  createdByUserId: z.string(),
  revokedByUserId: z.string().nullable(),
  lastUsedAt: z.string().datetime().nullable(),
  expiresAt: z.string().datetime().nullable(),
  revokedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type ApiKeyMetadata = z.infer<typeof ApiKeyMetadataSchema>

export const ApiSessionContextResponseSchema = z.object({
  auth: z.discriminatedUnion("type", [
    z.object({
      type: z.literal("user"),
      userId: z.string(),
    }),
    z.object({
      type: z.literal("api_key"),
      apiKeyId: z.string().uuid(),
      organizationId: z.string().uuid(),
      scopes: z.array(z.enum(apiKeyScopes)),
    }),
  ]),
  organizations: z.array(ApiOrganizationSchema),
  workspaces: z.array(ApiWorkspaceSchema),
  defaultOrganizationId: z.string().uuid().nullable(),
  defaultWorkspaceId: z.string().uuid().nullable(),
  /** True when the caller is a LedgerBox platform operator; gates the
   * internal /admin console in the web app. Always false for API keys. */
  platformAdmin: z.boolean(),
})

export type ApiSessionContextResponse = z.infer<
  typeof ApiSessionContextResponseSchema
>

export const ApiOrganizationListResponseSchema = z.object({
  organizations: z.array(ApiOrganizationSchema),
})

export type ApiOrganizationListResponse = z.infer<
  typeof ApiOrganizationListResponseSchema
>

export const ApiUpdateOrganizationBodySchema = z.object({
  name: z.string().min(1).max(200).optional(),
  billingEmail: z.string().email().nullable().optional(),
  // null clears the default (fresh sessions fall back to the first active
  // workspace); a uuid must be an active workspace of this organization.
  defaultWorkspaceId: z.string().uuid().nullable().optional(),
  collectsFrom: z.enum(organizationCollectsFromValues).nullable().optional(),
  /** Stamps onboardingCompletedAt once; ignored when it is already set. */
  onboardingCompleted: z.literal(true).optional(),
})

export type ApiUpdateOrganizationBody = z.infer<
  typeof ApiUpdateOrganizationBodySchema
>

export const ApiOrganizationResponseSchema = z.object({
  organization: ApiOrganizationSchema,
})

export type ApiOrganizationResponse = z.infer<
  typeof ApiOrganizationResponseSchema
>

export const ApiWorkspaceListResponseSchema = z.object({
  workspaces: z.array(ApiWorkspaceSchema),
})

export type ApiWorkspaceListResponse = z.infer<
  typeof ApiWorkspaceListResponseSchema
>

export const ApiCreateWorkspaceBodySchema = z.object({
  name: z.string().min(1).max(200),
  slug: ApiSlugSchema,
  clientCompanyName: z.string().min(1).max(200).optional(),
  externalReference: z.string().max(200).optional(),
  clientNotes: z.string().max(2000).optional(),
  /** True marks the workspace as a client and stamps clientSince. */
  isClient: z.boolean().optional(),
})

export type ApiCreateWorkspaceBody = z.infer<
  typeof ApiCreateWorkspaceBodySchema
>

export const ApiUpdateWorkspaceBodySchema = z.object({
  name: z.string().min(1).max(200).optional(),
  slug: ApiSlugSchema.optional(),
  clientCompanyName: z.string().min(1).max(200).nullable().optional(),
  externalReference: z.string().max(200).nullable().optional(),
  clientNotes: z.string().max(2000).nullable().optional(),
  /** A contact of this workspace; null clears the choice. */
  primaryContactId: z.string().uuid().nullable().optional(),
  /** True stamps clientSince when it is not set. False clears it. */
  isClient: z.boolean().optional(),
}).extend(ClientBrandFieldsSchema.shape)

export type ApiUpdateWorkspaceBody = z.infer<
  typeof ApiUpdateWorkspaceBodySchema
>

export const ApiWorkspaceResponseSchema = z.object({
  workspace: ApiWorkspaceSchema,
})

export type ApiWorkspaceResponse = z.infer<typeof ApiWorkspaceResponseSchema>

export const ApiOrganizationMemberListResponseSchema = z.object({
  members: z.array(ApiOrganizationMemberSchema),
})

export type ApiOrganizationMemberListResponse = z.infer<
  typeof ApiOrganizationMemberListResponseSchema
>

export const ApiCreateOrganizationMemberBodySchema = z.object({
  userId: z.string().min(1).max(200),
  role: z.enum(organizationRoles).default("member"),
})

export type ApiCreateOrganizationMemberBody = z.infer<
  typeof ApiCreateOrganizationMemberBodySchema
>

export const ApiUpdateOrganizationMemberBodySchema = z.object({
  role: z.enum(organizationRoles),
})

export type ApiUpdateOrganizationMemberBody = z.infer<
  typeof ApiUpdateOrganizationMemberBodySchema
>

export const ApiOrganizationMemberResponseSchema = z.object({
  member: ApiOrganizationMemberSchema,
})

export type ApiOrganizationMemberResponse = z.infer<
  typeof ApiOrganizationMemberResponseSchema
>

export const ApiKeyListResponseSchema = z.object({
  apiKeys: z.array(ApiKeyMetadataSchema),
})

export type ApiKeyListResponse = z.infer<typeof ApiKeyListResponseSchema>

// TODO(LED-46-placeholder): Documents v2 design imagines scopes like
// `batches:write`, `postings:write`, `documents:read`. Backend ships
// `upload | process | status | export | download`. UI renders backend names
// verbatim — do not translate. Rename when scope vocabulary is reviewed.
export const ApiCreateApiKeyBodySchema = z.object({
  name: z.string().min(1).max(200),
  scopes: z.array(z.enum(apiKeyScopes)).min(1),
  expiresAt: z.string().datetime().optional(),
})

export type ApiCreateApiKeyBody = z.infer<typeof ApiCreateApiKeyBodySchema>

export const ApiCreateApiKeyResponseSchema = z.object({
  apiKey: ApiKeyMetadataSchema,
  secret: z.string(),
})

export type ApiCreateApiKeyResponse = z.infer<
  typeof ApiCreateApiKeyResponseSchema
>

export const ApiRevokeApiKeyResponseSchema = z.object({
  apiKey: ApiKeyMetadataSchema,
})

export type ApiRevokeApiKeyResponse = z.infer<
  typeof ApiRevokeApiKeyResponseSchema
>
